import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SCHOOL_NAV } from "./SchoolDashboard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useNavigate } from "react-router-dom";
import { RotateCw } from "lucide-react";

const BADGES = {
  delivered:   "bg-emerald-100 text-emerald-700",
  shipped:     "bg-blue-100 text-blue-700",
  packed:      "bg-amber-100 text-amber-700",
  processing:  "bg-slate-100 text-slate-700",
  cancelled:   "bg-rose-100 text-rose-700",
};

export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState({});
  const cart = useCart();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [os, ps] = await Promise.all([api.get("/orders"), api.get("/products")]);
        setOrders(os.data || []);
        const map = {};
        (ps.data || []).forEach((p) => { map[p.id] = p; });
        setProducts(map);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const reorder = (o) => {
    let added = 0;
    for (const it of (o.items || [])) {
      const p = products[it.product_id];
      if (p) { cart.add(p); for (let i = 1; i < it.qty; i++) cart.add(p); added++; }
    }
    if (added === 0) {
      toast.error("These products are no longer available.");
      return;
    }
    toast.success(`${added} item(s) added to cart`);
    nav("/school/order");
  };

  return (
    <DashboardLayout nav={SCHOOL_NAV} title="Order History">
      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
          No orders yet. Start with <strong className="text-brand-blue">Place Order</strong>.
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3">Order #</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const status = (o.delivery_status || "processing").toLowerCase();
                return (
                  <tr key={o.id} data-testid={`row-${o.id}`} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold text-brand-navy">{o.order_number}</td>
                    <td className="px-4 py-3 text-slate-600">{new Date(o.created_at).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3 text-slate-600">{(o.items || []).length}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${BADGES[status] || BADGES.processing}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`btn-reorder-${o.id}`}
                        onClick={() => reorder(o)}
                        className="inline-flex items-center gap-1 text-xs text-brand-blue font-semibold hover:text-brand-blueDark"
                      >
                        <RotateCw size={12} /> Reorder
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
}
